// React
import {
  useState,
  useEffect,
  useContext,
  memo,
  forwardRef,
  useImperativeHandle,
} from "react";

// React router
import { useHistory } from "react-router";

// Api endpoints, hooks, and utility functions
import { getApplications } from "../../../api/endpoints/application";
import UpdateApplication from "./UpdateApplication/UpdateApplication";
import { useTranslation } from "react-i18next";
import {
  getGenericLocalStorage,
  setGenericLocalStorage,
} from "../../../utils/localSave";
import { useQuery } from "react-query";
import useTimeAgo from "../../hooks/useTimeAgo";

// Assets
import anonymousAvatar from "../../../assets/icons/anonymous-avatar.svg";

// Components
import { CurriculumVitae, Icon } from "../../../components";
import { Col, Grid, Row } from "../../../components/Grid";
import { Typography } from "../../../components/Typography";
import { IsLoading } from "../../style";
import {
  ApplicationWrapper,
  ApplicationTopBar,
  ApplicantMeta,
  ApplicantHeadings,
  ApplicantAvatar,
} from "./Application.styled";
import ApplicationStars from "./ApplicationStars/ApplicationStars";
import { ApplicantContactInfo } from "./ApplicantContactInfo/ApplicantContactInfo";
import StatusSelector from "./StatusSelector/StatusSelector";
import CVRating from "../../../components/CurriculumVitae/CVRating";
import ApplicationActionModal from "../../components/ApplicationCard/ApplicationActionModal";
import { UpdateLoading } from "../../../components/Loader/style";

// State management
import { DispatchErrorContext } from "../../../context/ErrorContext";
import {
  StatePositionContext,
  DispatchPositionContext,
} from "../../context/PositionContext";
// import { use } from "react";

const Application = memo(
  forwardRef(
    ({ manageShareholder, application, index, allApplications }, ref) => {
      const { t } = useTranslation();
      const positionState = useContext(StatePositionContext);
      const positionDispatch = useContext(DispatchPositionContext);
      let id = positionState.appId;
      let history = useHistory();
      const [editView, setEditView] = useState(true);
      const [single, setSingle] = useState(null);
      const [positionTitle, setPositionTitle] = useState("");
      const [next, setNext] = useState(null);
      const [prev, setPrev] = useState(null);
      const [createdAt, setCreatedAt] = useState(null);
      const [count, setCount] = useState();
      const [avatar, setAvatar] = useState(anonymousAvatar);
      const [confirmation, setConfirmation] = useState(false);
      const [allApplicationsArray, setAllApplicationsArray] = useState(
        allApplications?.data
      );
      const [filteredApplications, setFilteredApplications] = useState([]);
      const dispatchError = useContext(DispatchErrorContext);
      const isAnonymous = positionState.single.anon;

      useEffect(() => {
        //init edit view
        const admin_settings = getGenericLocalStorage("admin_settings", true);
        if (admin_settings && admin_settings.applicationEditViewOpen) {
          setEditView(admin_settings.applicationEditViewOpen === "open");
        }
      }, []);

      useEffect(() => {
        let admin_settings = getGenericLocalStorage("admin_settings", true);
        admin_settings = {
          ...admin_settings,
          applicationEditViewOpen: editView ? "open" : "closed",
        };
        setGenericLocalStorage("admin_settings", admin_settings, true);
      }, [editView]);

      const updateParentState = (newState) => {
        setConfirmation(newState);
      };

      //React query fetching of single application
      const fetchSingleApplication = async () => {
        const request = {
          selector: id,
          params: { includeSignedRequest: true },
        };
        return await getApplications(request);
      };
      const { data, isLoading, isError } = useQuery(
        ["single_application", id],
        fetchSingleApplication
      );

      useEffect(() => {
        //Handle error
        if (isError) {
          dispatchError({
            type: "set_error",
            payload: t("error.application.could_not_get_application"),
          });
          history.push("/404");
        }

        if (application) {
          setSingle(application[index]);
          setPositionTitle(application[index].position.title);
        } else if (data?.data[0]) {
          // setSingle(data.data[0]);
          setSingle(
            // Set the single to the allApplications data if available
            // Find the application in allApplications that matches the id of the data
            allApplications?.data?.find((app) => app.id === data?.data[0]?.id)
          );

          //Set position title state
          setPositionTitle(data.data[0].position.title);

          //Set next and prev pointers
          setNext(data.next);
          setPrev(data.prev);

          // Set createdAt for calculating time ago
          setCreatedAt(data.data[0].createdAt);
        }
      }, [data, application, index, ref]);

      useEffect(() => {
        // Return early if no allApplications
        if (!allApplications) {
          return;
        }

        // Set allApplicationsArray to the data from allApplications
        setAllApplicationsArray(allApplications.data);

        // Update the single application if it exists in allApplications
        if (single) {
          const updatedSingle = allApplications.data.find(
            (app) => app.id === single.id
          );
          if (updatedSingle) {
            setSingle(updatedSingle);
          }
        }
      }, [allApplications]);

      // Format days weeks month
      const timeAgo = useTimeAgo(createdAt);

      useEffect(() => {
        if (single) {
          if (single.externalProfileImage) {
            setAvatar(single.externalProfileImage);
            return;
          }

          const { files } = single;

          if (files) {
            const profileImage = files.find(
              (fileObject) => fileObject.docType === "profileImage"
            );

            if (profileImage) {
              setAvatar(profileImage.signedRequest);
            } else {
              setAvatar(anonymousAvatar);
            }
          }
        }
      }, [single]);

      useEffect(() => {
        // Return early if no allApplications or single
        if (!allApplications || !single) {
          return;
        }

        // Use the filtered applications array
        const filteredApplications = allApplicationsArray.filter((app) => {
          // If the single application has a status of rejected
          if (single.status === "rejected") {
            // Filter out all applications with a status of rejected
            return app.status === single.status;
          } else if (
            // Else if the single application has a status of invited or shortlisted
            single.status === "invited" ||
            single.substatus === "shortlisted"
          ) {
            // Filter out all applications with a status of invited or substatus of shortlisted
            return app.status === "invited" || app.substatus === "shortlisted";
          } else {
            // For all other statuses, filter based on substatus
            return (
              app.substatus === single.substatus ||
              (single.substatus === "applied" &&
                app.substatus === null &&
                app.status !== "invited") ||
              (single.substatus === null && app.substatus === "applied")
            );
          }
        });

        // Set the filtered applications to the state
        setFilteredApplications(filteredApplications);

        // Get the index of the selected application
        const selectedIndex = filteredApplications?.findIndex(
          (app) => app.id === single.id
        );

        // Set count based on the index
        setCount(selectedIndex + 1);
      }, [single]);

      // Handle counter
      function handleNext() {
        // Return early if no allApplicationsArray or single
        if (!allApplicationsArray || !single) {
          return;
        }

        // Use the filtered applications array
        const filteredApplications = allApplicationsArray.filter((app) => {
          // If the single application has a status of rejected
          if (single.status === "rejected") {
            // Filter out all applications with a status of rejected
            return app.status === single.status;
          } else if (
            // Else if the single application has a status of invited or shortlisted
            single.status === "invited" ||
            single.substatus === "shortlisted"
          ) {
            // Filter out all applications with a status of invited or substatus of shortlisted
            return app.status === "invited" || app.substatus === "shortlisted";
          } else {
            // For all other statuses, filter based on substatus
            return (
              app.substatus === single.substatus ||
              (single.substatus === "applied" &&
                app.substatus === null &&
                app.status !== "invited") ||
              (single.substatus === null && app.substatus === "applied")
            );
          }
        });

        if (!filteredApplications || filteredApplications.length === 0) {
          return;
        }

        const currentIndex = filteredApplications.findIndex(
          (app) => app.id === single.id
        );

        const nextIndex = (currentIndex + 1) % filteredApplications.length;

        const nextApplication = filteredApplications[nextIndex];

        setCount(nextIndex + 1); // Update count first

        positionDispatch({
          type: "application_id",
          payload: nextApplication.id,
        });
      }

      function handlePrev() {
        // Return early if no allApplicationsArray or single
        if (!allApplicationsArray || !single) {
          return;
        }

        // Use the filtered applications array
        const filteredApplications = allApplicationsArray.filter((app) => {
          // If the single application has a status of rejected
          if (single.status === "rejected") {
            // Filter out all applications with a status of rejected
            return app.status === single.status;
          } else if (
            // Else if the single application has a status of invited or shortlisted
            single.status === "invited" ||
            single.substatus === "shortlisted"
          ) {
            // Filter out all applications with a status of invited or substatus of shortlisted
            return app.status === "invited" || app.substatus === "shortlisted";
          } else {
            // For all other statuses, filter based on substatus
            return (
              app.substatus === single.substatus ||
              (single.substatus === "applied" &&
                app.substatus === null &&
                app.status !== "invited") ||
              (single.substatus === null && app.substatus === "applied")
            );
          }
        });

        if (!filteredApplications || filteredApplications.length === 0) {
          return;
        }

        const currentIndex = filteredApplications.findIndex(
          (app) => app.id === single.id
        );
        const prevIndex =
          (currentIndex - 1 + filteredApplications.length) %
          filteredApplications.length;
        const prevApplication = filteredApplications[prevIndex];

        setCount(prevIndex + 1); // Update count first

        positionDispatch({
          type: "application_id",
          payload: prevApplication.id,
        });
      }

      function handleCurrent(allApplicationsArray, result) {
        // Return early if no allApplicationsArray or single
        if (!allApplicationsArray || !single) {
          return;
        }

        // Check if the dragged application (result) has the same substatus as the current single or the dragged application is dragged to the same substatus as the current single (DRAGGED TO OR FROM CURRENT SINGLE SUBSTATUS)
        if (
          single.substatus === result.destination.droppableId ||
          single.substatus === result.source.droppableId
        ) {
          // If the dragged application is the current single
          if (result.draggableId.toString() === single.id.toString()) {
            const filteredApplications = allApplicationsArray.filter((app) => {
              if (result.destination.droppableId === "shortlisted") {
                return (
                  app.status === "invited" || app.substatus === "shortlisted"
                );
              } else {
                return (
                  app.substatus === result.destination.droppableId ||
                  (result.destination.droppableId === "applied" &&
                    app.substatus === null &&
                    app.status !== "invited") ||
                  (result.destination.droppableId === null &&
                    app.substatus === "applied")
                );
              }
            });

            if (!filteredApplications || filteredApplications.length === 0) {
              return;
            }

            const currentIndex = filteredApplications.findIndex(
              (app) => app.id.toString() === result.draggableId.toString()
            );

            setCount(currentIndex + 1);

            setFilteredApplications(filteredApplications);

            // Update the local substatus of the dragged application
            single.substatus = result.destination.droppableId;

            if (result.draggableId.toString() === single.id.toString()) {
              positionDispatch({
                type: "application_id",
                payload: parseInt(result.draggableId, 10),
              });
            }
          }

          // When dragging another application that is not currently selected (NOT THE CURRENT SINGLE)
          if (result.draggableId.toString() !== single.id.toString()) {
            let filteredApplications = [];

            // CHECK IF "NOT CURRENT" ELEMENT IS DRAGGED TO THE SAME AS THE CURRENT SINGLE
            if (single.substatus !== result.destination.droppableId) {
              // ELSE DO ANOTHER FILTER FOR WHEN THE "NOT CURRENT" ELEMENT IS DRAGGED TO ANOTHER STATUS
              filteredApplications = allApplicationsArray.filter((app) => {
                if (result.source.droppableId === "shortlisted") {
                  return (
                    app.status === "invited" || app.substatus === "shortlisted"
                  );
                } else {
                  return (
                    app.substatus === result.source.droppableId ||
                    (result.source.droppableId === "applied" &&
                      app.substatus === null &&
                      app.status !== "invited") ||
                    (result.source.droppableId === null &&
                      app.substatus === "applied")
                  );
                }
              });
            } else {
              // ELSE THE "NOT CURRENT" ELEMENT IS DRAGGED OUT OF THE CURRENT SINGLE
              // DO THIS IF THE DRAGGED ELEMENT IS DRAGGING TO THE SAME AS THE CURRENT SINGLE
              filteredApplications = allApplicationsArray.filter((app) => {
                if (result.destination.droppableId === "shortlisted") {
                  return (
                    app.status === "invited" || app.substatus === "shortlisted"
                  );
                } else {
                  return (
                    app.substatus === result.destination.droppableId ||
                    (result.destination.droppableId === "applied" &&
                      app.substatus === null &&
                      app.status !== "invited") ||
                    (result.destination.droppableId === null &&
                      app.substatus === "applied")
                  );
                }
              });
            }

            if (!filteredApplications || filteredApplications.length === 0) {
              return;
            }

            const currentIndex = filteredApplications.findIndex(
              (app) => app.id === single.id
            );

            setCount(currentIndex + 1);

            setFilteredApplications(filteredApplications);

            positionDispatch({
              type: "application_id",
              payload: single.id,
            });
          }
        }
      }

      // Modal for application buttons confirmation
      const [modalAction, setModalAction] = useState(null);
      const handleActionClick = (actionName) => {
        setModalAction(actionName);
      };

      const updateSingleInParent = (updatedApplication, goNext) => {
        const updatedAllApplicationsArray = allApplicationsArray.map((app) =>
          app.id === updatedApplication.id
            ? { ...app, ...updatedApplication }
            : app
        );

        setAllApplicationsArray(updatedAllApplicationsArray);

        setSingle(updatedApplication);

        if (goNext) {
          handleChange(updatedApplication);
          goNextOrStay(updatedApplication);
        }
      };

      const handleChange = (updatedApplication) => {
        // Update the allApplicationsArray with the updated application
        const updatedAllApplicationsArray = allApplicationsArray.map((app) => {
          if (app.id === updatedApplication.id) {
            return {
              ...app,
              substatus: updatedApplication.substatus,
            };
          }
          return app;
        });

        // Update the state with the new array
        setAllApplicationsArray(updatedAllApplicationsArray);
      };

      const goNextOrStay = (updatedApplication) => {
        if (!allApplicationsArray || !single) {
          return;
        }

        // Use the filtered applications array
        const filteredApplications = allApplicationsArray.filter((app) => {
          // If the single application has a status of rejected
          if (single.status === "rejected") {
            // Filter out all applications with a status of rejected
            return app.status === single.status;
          } else if (
            // Else if the single application has a status of shortlisted
            single.substatus === "shortlisted"
          ) {
            // Filter out all applications with a status of invited or substatus of shortlisted
            return app.substatus === "shortlisted";
          } else {
            // For all other statuses, filter based on substatus
            return (
              app.substatus === single.substatus ||
              (single.substatus === "applied" &&
                app.substatus === null &&
                app.status !== "invited") ||
              (single.substatus === null && app.substatus === "applied")
            );
          }
        });

        // Remove the object with the same id as single.applicantId
        const filteredApplicationsWithoutCurrent = filteredApplications.filter(
          (app) => {
            return app.id !== single.id;
          }
        );

        setFilteredApplications(filteredApplicationsWithoutCurrent);

        if (
          !filteredApplicationsWithoutCurrent ||
          filteredApplicationsWithoutCurrent.length === 0
        ) {
          // Use the filtered applications array
          const filteredApplications = allApplicationsArray.filter((app) => {
            // If the single application has a status of rejected
            if (updatedApplication.status === "rejected") {
              // Filter out all applications with a status of rejected
              return app.status === updatedApplication.status;
            } else if (
              // Else if the single application has a status of invited or shortlisted
              updatedApplication.status === "invited" ||
              updatedApplication.substatus === "shortlisted"
            ) {
              // Filter out all applications with a status of invited or substatus of shortlisted
              return (
                app.status === "invited" || app.substatus === "shortlisted"
              );
            } else {
              // For all other statuses, filter based on substatus
              return (
                app.substatus === updatedApplication.substatus ||
                (updatedApplication.substatus === "applied" &&
                  app.substatus === null &&
                  app.status !== "invited") ||
                (updatedApplication.substatus === null &&
                  app.substatus === "applied")
              );
            }
          });

          // Set the filtered applications to the state
          setFilteredApplications(filteredApplications);
          return;
        } else {
          handleNext();
        }
      };

      useImperativeHandle(ref, () => ({
        onDragEndHandler(result) {
          handleCurrent(allApplicationsArray, result);
        },
      }));

      return (
        <>
          {confirmation && (
            <UpdateLoading success>
              {t("common.emailCopied", "E-mail copied to clipboard")}
            </UpdateLoading>
          )}
          {modalAction && (
            <ApplicationActionModal
              setModalAction={(state) => {
                setModalAction(state);
                if (modalAction === "delete") {
                  handleNext();
                }
              }}
              modalAction={modalAction}
              applications={[single]}
            />
          )}
          <ApplicationWrapper
            applicantView={positionState.candidateView && !manageShareholder}
            manageShareholder={manageShareholder}
          >
            {!isLoading && single && single.applicant ? (
              <>
                {!manageShareholder && (
                  <ApplicationTopBar>
                    <>
                      <div>
                        <Icon
                          icon="arrow_left"
                          mr="24"
                          hideOnDesktop
                          pointer
                          onClick={() =>
                            positionDispatch({
                              type: "candidate_view",
                              payload: false,
                            })
                          }
                        />
                        <Typography tag="p" nomargin large color="#52576A">
                          {timeAgo}
                        </Typography>
                      </div>
                      <div>
                        <Typography
                          tag="p"
                          nomargin
                          large
                          color="#52576A"
                        >{`${count} ${t("common.outOf", "out of")} ${
                          filteredApplications &&
                          filteredApplications.length > 0
                            ? filteredApplications.length
                            : 1
                        }`}</Typography>
                        <Icon
                          icon="next_prev"
                          onClick={handlePrev}
                          ml="24"
                          pointer
                        />
                        <Icon
                          icon="next_prev"
                          onClick={handleNext}
                          ml="24"
                          pointer
                          scale="-1"
                        />
                        <Icon
                          icon="collapse"
                          scale="-1"
                          ml="48"
                          hideOnMobile
                          pointer
                          onClick={() =>
                            positionDispatch({
                              type: "candidate_view",
                              payload: false,
                            })
                          }
                        />
                      </div>
                    </>
                  </ApplicationTopBar>
                )}
                <Grid
                  fluid
                  flex
                  heightSM="calc(100% - 70px - 64px);"
                  heightMD="calc(100% - 70px - 44px);"
                  manageShareholder={manageShareholder}
                >
                  <Row
                    height="100%"
                    width="250px"
                    flexDirectionMD="column"
                    plMD="44"
                    prMD="44"
                    ptMD="26"
                    bg="rgba(243, 243, 245, 0.6);"
                    ml="0"
                    mr="0"
                    hideOnMobile
                    application
                  >
                    <ApplicantAvatar
                      large
                      avatar={isAnonymous ? anonymousAvatar : avatar}
                    />
                    <ApplicantContactInfo
                      single={single}
                      isAnonymous={isAnonymous}
                      onUpdateState={updateParentState}
                    />
                    {Object.keys(single.competencies).length > 0 && (
                      <>
                        <Typography
                          tag="p"
                          bold
                          fontWeightLG="600"
                          nomargin
                          mb="10"
                          mt="15"
                        >
                          {t("common.skills", "Skills")}
                        </Typography>
                        <CVRating single={single.competencies} />
                      </>
                    )}
                    {Object.keys(single.languages).length > 0 && (
                      <>
                        <Typography
                          tag="p"
                          bold
                          fontWeightLG="600"
                          nomargin
                          mb="10"
                          mt="15"
                        >
                          {t("common.languages", "Languages")}
                        </Typography>
                        <CVRating single={single.languages} />
                      </>
                    )}
                  </Row>
                  <Row
                    ml="0"
                    mr="0"
                    pb="50"
                    overflow="hidden"
                    height="100%"
                    widthMD="calc(100% - 250px)"
                    cv
                    manageShareholder
                  >
                    <Col mlMD="26" mrMD="26">
                      <ApplicantMeta>
                        <ApplicantAvatar
                          avatar={isAnonymous ? anonymousAvatar : avatar}
                          hideOnDesktop
                        />
                        <ApplicantHeadings>
                          <Typography
                            mbLG="12"
                            fontWeightLG="300"
                            tag="p"
                            small
                            uppercase
                            bold
                            lowOpacity
                            letterSpaced
                            nomargin
                            mb="5"
                          >
                            {single.position.title}
                          </Typography>
                          <Typography
                            tag="h1"
                            customSize="20"
                            capitalize
                            mb="5"
                            isAnonymous={isAnonymous}
                          >{`${single.applicant.candidate.firstName} ${single.applicant.candidate.lastName}`}</Typography>
                          <ApplicationStars
                            id={single}
                            disabled={manageShareholder}
                          />
                        </ApplicantHeadings>
                        {!manageShareholder &&
                          single.status !== "invited" &&
                          single.status !== "rejected" && (
                            <StatusSelector
                              application={single}
                              hideOnMobile
                              handleChange={handleChange}
                            />
                          )}
                      </ApplicantMeta>
                      <ApplicantContactInfo single={single} hideOnDesktop />
                      <CurriculumVitae data={single} />
                    </Col>
                  </Row>
                </Grid>
                {!manageShareholder && (
                  <UpdateApplication
                    single={single}
                    allApplicationsArray={allApplicationsArray}
                    handleActionClick={handleActionClick}
                    updateSingleInParent={updateSingleInParent}
                  />
                )}
              </>
            ) : (
              <IsLoading />
            )}
          </ApplicationWrapper>
        </>
      );
    }
  )
);

export default Application;
